import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_SECURITY_SERVERS } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import {
  AntimalwareSecurityServersOnDemandSlotsValues,
  AntimalwareSecurityServersOrder,
  AntimalwareSecurityServersSvaProxyProfile,
  ModuleId,
  SectionId,
} from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { toggleDisabled, toggleVisibility } from '../../../../../../../utils/htmlElement.utils';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import {
  AntimalwareSecurityServersFormValue,
  AntimalwareSecurityServersSearchableKeywords,
} from '../../../../utils/uiSettings/antimalware/antimalware.types';
import {
  ANTIMALWARE_SECURITY_SERVERS_DEFAULT_VALUE,
  getAntimalwareSecurityServersOnDemandSlotsValuesOptions,
  getAntimalwareSecurityServersSearchableKeywords,
  MAX_ANTIMALWARE_SECURITY_SERVERS_ON_DEMAND_SLOTS,
  MIN_ANTIMALWARE_SECURITY_SERVERS_ON_DEMAND_SLOTS,
} from '../../../../utils/uiSettings/antimalware/antimalware.values';
import { FontSizes, FontWeights } from '../../../../../../../types/common/types';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';
import { GzRadioGroupWebComponent } from '../../../../../../../ui/gzRadio';
import { classMap } from 'lit/directives/class-map.js';
import { GzDropdownWebComponent } from '../../../../../../../ui/gzDropdown';
import { GzInputNumberWebComponent, InputWidthValues } from '../../../../../../../ui/gzInput';
import { GzAntimalwareSecurityServersSvaIpsTableWebComponent } from '../gridComponents/GzAntimalwareSecurityServersSvaIpsTableWebComponent';
import { RequiredValidator } from '../../../../../../../ui/gzForm/validators/required.validator';

@customElement(WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_SECURITY_SERVERS)
export class GzAntimalwareSecurityServersWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  AntimalwareSecurityServersFormValue,
  AntimalwareSecurityServersSearchableKeywords
> {
  protected path = {
    moduleId: ModuleId.ANTIMALWARE,
    sectionId: SectionId.ANTIMALWARE_SECURITY_SERVERS,
  };
  protected searchableKeywords = getAntimalwareSecurityServersSearchableKeywords();

  @query('#antimalware-security-servers-sva-ips-field')
  private securityServersSvaIpsField: GzFormFieldWebComponent<GzAntimalwareSecurityServersSvaIpsTableWebComponent>;
  @query('#antimalware-security-servers-order-field')
  private securityServersOrderField: GzFormFieldWebComponent<GzRadioGroupWebComponent>;
  @query('#antimalware-security-servers-affinity-rule-field')
  private securityServersAffinityRuleField: GzFormFieldWebComponent<GzRadioGroupWebComponent>;
  @query('#antimalware-security-servers-use-ssl-field')
  private securityServersUseSslField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#antimalware-security-servers-sva-proxy-profile-field')
  private securityServersSvaProxyProfileField: GzFormFieldWebComponent<GzRadioGroupWebComponent>;
  @query('#antimalware-security-servers-sva-mp-affinity-rules-field')
  private securityServersSvaMpAffinityRulesField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#antimalware-security-servers-on-demand-slots-enable-field')
  private securityServersOnDemandSlotsEnableField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#antimalware-security-servers-on-demand-slots-values-field')
  private securityServersOnDemandSlotsValuesField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#antimalware-security-servers-on-demand-slots-field')
  private securityServersOnDemandSlotsField: GzFormFieldWebComponent<GzInputNumberWebComponent>;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html`<gz-title
      class="content"
      searchable-label="${this.searchableKeywords[AntimalwareSecurityServersSearchableKeywords.SecurityServers].label}"
      ><span
        >${this.searchableKeywords[AntimalwareSecurityServersSearchableKeywords.SecurityServers].translatedLabel}</span
      >
    </gz-title>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}" class="infoText"
      >${translateFn(
        'Assign Security Servers to endpoints and set their priority for performing scanning tasks. Using Security Servers is recommended for endpoints with low resources.'
      )}
    </gz-text>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div id="antimalware-anti-tampering-content">
        <!-- Security Servers Load Balancing -->
        <div id="antimalware-security-servers-assignment-content">
          <gz-title
            fontWeight="${FontWeights.SEMIBOLD}"
            fontSize="${FontSizes.PARAGRAPH_12}"
            class="mt-4 subtitle"
            searchable-label="${this.searchableKeywords[
              AntimalwareSecurityServersSearchableKeywords.SecurityServerAssignment
            ].label}"
            >${this.searchableKeywords[AntimalwareSecurityServersSearchableKeywords.SecurityServerAssignment]
              .translatedLabel}</gz-title
          >

          <gz-form-field id="antimalware-security-servers-sva-ips-field" class="mt-2">
            <gz-policy-panel-antimalware-security-servers-sva-ips-table
              class="hideButtonsOutsideGrid"
              gz-form-input
            ></gz-policy-panel-antimalware-security-servers-sva-ips-table>
          </gz-form-field>
        </div>

        <!-- Security Servers Load Balancing -->
        <div id="antimalware-security-servers-load-balancing-content">
          <gz-title
            fontWeight="${FontWeights.SEMIBOLD}"
            fontSize="${FontSizes.PARAGRAPH_12}"
            class="mt-4 subtitle"
            searchable-label="${this.searchableKeywords[
              AntimalwareSecurityServersSearchableKeywords.SecurityServerLoadBalancing
            ].label}"
            >${this.searchableKeywords[AntimalwareSecurityServersSearchableKeywords.SecurityServerLoadBalancing]
              .translatedLabel}</gz-title
          >

          <gz-form-field id="antimalware-security-servers-order-field" class="mt-2">
            <gz-radio-group
              gz-form-input
              class="d-flex flex-column"
              @radioSelectionChanged=${this.onServerOrderSelectionChanged}
            >
              <div>
                <gz-radio
                  searchable-label="${this.searchableKeywords[
                    AntimalwareSecurityServersSearchableKeywords.RedundancyMode
                  ].label}"
                  label="${this.searchableKeywords[AntimalwareSecurityServersSearchableKeywords.RedundancyMode]
                    .translatedLabel}"
                  .value=${AntimalwareSecurityServersOrder.REDUNDANCY}
                ></gz-radio>
                <gz-text
                  fontSize="${FontSizes.PARAGRAPH_12}"
                  fontWeight="${FontWeights.NORMAL}"
                  class="helper-text infoText"
                  >${translateFn('Sends scanning tasks to the first available Security Server in the list.')}</gz-text
                >
                <div class="mt-1">${this.renderAffinityRuleField()}</div>
              </div>

              <div>
                <gz-radio
                  searchable-label="${this.searchableKeywords[
                    AntimalwareSecurityServersSearchableKeywords.EqualDistributionMode
                  ].label}"
                  label="${this.searchableKeywords[AntimalwareSecurityServersSearchableKeywords.EqualDistributionMode]
                    .translatedLabel}"
                  .value=${AntimalwareSecurityServersOrder.BALANCED}
                ></gz-radio>

                <gz-text
                  fontSize="${FontSizes.PARAGRAPH_12}"
                  fontWeight="${FontWeights.NORMAL}"
                  class="helper-text infoText"
                  >${translateFn('Distributes the scanning load equally between Security Servers.')}</gz-text
                >
              </div>
            </gz-radio-group>
          </gz-form-field>
        </div>

        <!-- Communication between security servers and endpoints -->
        <div id="antimalware-security-servers-communication-endpoints">
          <gz-title
            fontWeight="${FontWeights.SEMIBOLD}"
            fontSize="${FontSizes.PARAGRAPH_12}"
            class="mt-4 subtitle"
            searchable-label="${this.searchableKeywords[
              AntimalwareSecurityServersSearchableKeywords.CommunicationEndpoints
            ].label}"
            >${this.searchableKeywords[AntimalwareSecurityServersSearchableKeywords.CommunicationEndpoints]
              .translatedLabel}</gz-title
          >

          <gz-form-field id="antimalware-security-servers-use-ssl-field" class="mt-2">
            <gz-checkbox
              searchable-label="${this.searchableKeywords[AntimalwareSecurityServersSearchableKeywords.UseSsl].label}"
              label="${this.searchableKeywords[AntimalwareSecurityServersSearchableKeywords.UseSsl].translatedLabel}"
              gz-form-input
            >
            </gz-checkbox>
          </gz-form-field>
        </div>

        <!-- Communication between security servers and GravityZone -->
        <div id="antimalware-security-servers-gravity-zone-endpoints">
          <gz-title
            fontWeight="${FontWeights.SEMIBOLD}"
            fontSize="${FontSizes.PARAGRAPH_12}"
            class="mt-4 subtitle"
            searchable-label="${this.searchableKeywords[
              AntimalwareSecurityServersSearchableKeywords.CommunicationGravityZone
            ].label}"
            >${this.searchableKeywords[AntimalwareSecurityServersSearchableKeywords.CommunicationGravityZone]
              .translatedLabel}</gz-title
          >

          <gz-form-field id="antimalware-security-servers-sva-proxy-profile-field" class="mt-2">
            <gz-radio-group gz-form-input class="d-flex flex-column">
              <gz-radio
                searchable-label="${this.searchableKeywords[
                  AntimalwareSecurityServersSearchableKeywords.KeepInstallation
                ].label}"
                label="${this.searchableKeywords[AntimalwareSecurityServersSearchableKeywords.KeepInstallation]
                  .translatedLabel}"
                .value=${AntimalwareSecurityServersSvaProxyProfile.KEEP_CURRENT_SETTINGS}
              ></gz-radio>

              <gz-radio
                searchable-label="${this.searchableKeywords[AntimalwareSecurityServersSearchableKeywords.UseProxy]
                  .label}"
                label="${this.searchableKeywords[AntimalwareSecurityServersSearchableKeywords.UseProxy]
                  .translatedLabel}"
                .value=${AntimalwareSecurityServersSvaProxyProfile.ENABLE_PROXY}
              ></gz-radio>

              <gz-radio
                searchable-label="${this.searchableKeywords[AntimalwareSecurityServersSearchableKeywords.DoNotUseProxy]
                  .label}"
                label="${this.searchableKeywords[AntimalwareSecurityServersSearchableKeywords.DoNotUseProxy]
                  .translatedLabel}"
                .value=${AntimalwareSecurityServersSvaProxyProfile.DISABLE_PROXY}
              ></gz-radio>
            </gz-radio-group>
          </gz-form-field>
        </div>

        <!-- Security Server Configuration -->
        <div id="antimalware-security-servers-configuration">
          <gz-title
            fontWeight="${FontWeights.SEMIBOLD}"
            fontSize="${FontSizes.PARAGRAPH_12}"
            class="mt-4 subtitle"
            searchable-label="${this.searchableKeywords[
              AntimalwareSecurityServersSearchableKeywords.SecurityServerConfiguration
            ].label}"
            >${this.searchableKeywords[AntimalwareSecurityServersSearchableKeywords.SecurityServerConfiguration]
              .translatedLabel}</gz-title
          >

          <gz-form-field
            id="antimalware-security-servers-sva-mp-affinity-rules-field"
            class="mt-2 ${classMap({ hidden: Application.Main.product.isCloud() })}"
          >
            <gz-tooltip
              tooltipText="${translateFn(
                'Enabling this setting will prevent live migration of Security Server Multi-Platform.'
              )}"
              ><gz-checkbox
                label="${translateFn('Enable affinity rules for Security Server Multi-Platform')}"
                gz-form-input
              >
              </gz-checkbox
            ></gz-tooltip>
          </gz-form-field>

          <div class="checkbox-with-input">
            <gz-form-field class="checkbox-width" id="antimalware-security-servers-on-demand-slots-enable-field">
              <gz-checkbox
                searchable-label="${this.searchableKeywords[AntimalwareSecurityServersSearchableKeywords.Limit].label}"
                label="${this.searchableKeywords[AntimalwareSecurityServersSearchableKeywords.Limit].translatedLabel +
                ':'}"
                gz-form-input
                @change="${e => {
                  toggleDisabled(this.securityServersOnDemandSlotsValuesField, !e.detail.checked);
                  toggleDisabled(this.securityServersOnDemandSlotsField, !e.detail.checked);
                }}"
              ></gz-checkbox>
              <gz-input-error></gz-input-error>
            </gz-form-field>

            <gz-form-field id="antimalware-security-servers-on-demand-slots-values-field" class="ml-2">
              <gz-dropdown
                gz-form-input
                .options="${getAntimalwareSecurityServersOnDemandSlotsValuesOptions()}"
                .value="${AntimalwareSecurityServersOnDemandSlotsValues.LOW}"
                @selectionChanged="${this.onDemandSlotsSelectionChanged.bind(this)}"
              ></gz-dropdown>
            </gz-form-field>

            <gz-form-field id="antimalware-security-servers-on-demand-slots-field" class="hidden ml-2" disabled>
              <gz-input-number
                gz-form-input
                inputWidth="${InputWidthValues.XSMALL}"
                placeholder="${translateFn('Select')}"
                min="${MIN_ANTIMALWARE_SECURITY_SERVERS_ON_DEMAND_SLOTS}"
                max="${MAX_ANTIMALWARE_SECURITY_SERVERS_ON_DEMAND_SLOTS}"
              ></gz-input-number>
            </gz-form-field>
          </div>
        </div>
      </div>
    `;
  }

  private renderAffinityRuleField() {
    return html`
      <gz-labeled-content
        class="helper-text ${classMap({ hidden: Application.Main.product.isCloud() })}"
        hasFormLabelWidth
        labelFontSize="${FontSizes.HEADING_6}"
        labelFontWeight="${FontWeights.NORMAL}"
        label="${translateFn('First connect to') + ':'}"
      >
        <gz-form-field
          id="antimalware-security-servers-affinity-rule-field"
          @radioSelectionChanged=${e => e.stopPropagation()}
        >
          <gz-radio-group gz-form-input class="d-flex flex-column">
            <div>
              <gz-radio label="${translateFn('Security Server with highest priority')}" .value=${false}></gz-radio>
            </div>

            <div>
              <gz-radio
                label="${translateFn('Security Server installed on the same physical host, if available')}"
                .value=${true}
              ></gz-radio>

              <gz-text
                fontSize="${FontSizes.PARAGRAPH_12}"
                fontWeight="${FontWeights.NORMAL}"
                class="helper-text infoText"
                >${translateFn(
                  'By default, scanning uses the Security Server on the same host as the target endpoint. If unavailable, other assigned Security Servers are used based on priority.'
                )}</gz-text
              >
            </div>
          </gz-radio-group>
        </gz-form-field>
      </gz-labeled-content>
    `;
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.antimalware.securityServers
   */
  protected getValueFromManager(): AntimalwareSecurityServersFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [ModuleId.ANTIMALWARE]: {
          [SectionId.ANTIMALWARE_SECURITY_SERVERS]: ANTIMALWARE_SECURITY_SERVERS_DEFAULT_VALUE,
        },
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.ANTIMALWARE]: {
          [SectionId.ANTIMALWARE_SECURITY_SERVERS]: PolicyPanelManager.getInstance().getPolicy(
            'uiSettings.antimalware.securityServers'
          ),
        },
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        antimalware: {
          securityServers: {
            svaIps: this.securityServersSvaIpsField,
            svaProxy: {
              profile: this.securityServersSvaProxyProfileField,
            },
            affinityRule: this.securityServersAffinityRuleField,
            enableOnDemandSlots: this.securityServersOnDemandSlotsEnableField,
            onDemandSlotsValues: this.securityServersOnDemandSlotsValuesField,
            onDemandSlots: this.securityServersOnDemandSlotsField,
            useSSL: this.securityServersUseSslField,
            svaMpAffinityRules: this.securityServersSvaMpAffinityRulesField,
            serverOrder: this.securityServersOrderField,
          },
        },
      },
    });
  }

  /**
   * Set validators for form fields.
   */
  protected setValidators() {
    this.securityServersOnDemandSlotsField.Validators = [
      RequiredValidator(
        translateFn('Enter the limit of concurrent scans. Only numbers between 1 and 1024 are allowed.')
      ),
    ];
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        antimalware: {
          securityServers: this.valueT.uiSettings.antimalware.securityServers,
        },
      },
    };
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'antimalware', 'securityServers'], {});
    }

    return value;
  }

  private onServerOrderSelectionChanged(e: CustomEvent) {
    toggleDisabled(
      this.securityServersAffinityRuleField,
      e.detail.selectedValue === AntimalwareSecurityServersOrder.BALANCED
    );

    if (e.detail.selectedValue === AntimalwareSecurityServersOrder.BALANCED) {
      this.securityServersAffinityRuleField.value = false;
    }
  }

  private onDemandSlotsSelectionChanged(e: CustomEvent) {
    const isCustom = e.detail.value === AntimalwareSecurityServersOnDemandSlotsValues.CUSTOM;
    this.securityServersOnDemandSlotsField.disabled = !isCustom;
    this.securityServersOnDemandSlotsField.validate(true);
    toggleVisibility(this.securityServersOnDemandSlotsField, isCustom);
  }

  /**
   * Override reset method, because we need to reset also slots component.
   */
  public reset() {
    super.reset();
    this.securityServersOnDemandSlotsField.value = '';
    this.securityServersSvaIpsField.Input.reset();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_SECURITY_SERVERS: GzAntimalwareSecurityServersWebComponent;
  }
}
